/**
 * GrowthPixelHealth — Pixel/CAPI health with visual score (premium design)
 */
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Zap, AlertTriangle, CheckCircle, TrendingUp } from "lucide-react";

const HealthScore = ({ status, divergence }) => {
  const getScore = () => {
    if (status === "healthy") return 100 - divergence;
    if (status === "warning") return 70 - (divergence / 2);
    return 40 - (divergence / 2);
  };

  const score = Math.max(0, Math.min(100, getScore()));
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg width="160" height="160" className="transform -rotate-90">
        <circle
          cx="80"
          cy="80"
          r="45"
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="8"
        />
        <circle
          cx="80"
          cy="80"
          r="45"
          fill="none"
          stroke={status === "healthy" ? "#10b981" : status === "warning" ? "#f59e0b" : "#ef4444"}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-3xl font-bold text-slate-900">{Math.round(score)}</p>
          <p className="text-xs font-semibold text-slate-600">Score</p>
        </div>
      </div>
    </div>
  );
};

export default function GrowthPixelHealth({ authAxios, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPixelHealth = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/pixel-health?days=${days}`);
        setData(res.data);
      } catch (err) {
        console.error("Error fetching pixel health:", err);
        toast.error("Erro ao carregar Pixel");
      } finally {
        setLoading(false);
      }
    };

    fetchPixelHealth();
  }, [days, authAxios]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-48 w-48 mx-auto rounded-full" />
          </CardContent>
        </Card>
        <div className="grid md:grid-cols-2 gap-4">
          {Array(2).fill(0).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Sem dados de Pixel</p>
        </CardContent>
      </Card>
    );
  }

  const {
    total_events,
    api_events,
    browser_events,
    divergence_percent,
    status
  } = data;

  const statusConfig = {
    healthy: {
      label: "Saudável",
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      badge: "bg-green-100 text-green-700"
    },
    warning: {
      label: "Atenção",
      icon: AlertTriangle,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      badge: "bg-yellow-100 text-yellow-700"
    },
    critical: {
      label: "Crítico",
      icon: AlertTriangle,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      badge: "bg-red-100 text-red-700"
    }
  };

  const config = statusConfig[status] || statusConfig.healthy;
  const Icon = config.icon;
  const apiPercent = ((api_events / Math.max(total_events, 1)) * 100).toFixed(1);
  const browserPercent = ((browser_events / Math.max(total_events, 1)) * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Health Score Card */}
      <Card className={`${config.bgColor} border ${config.borderColor}`}>
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Icon className={`w-5 h-5 ${config.color}`} />
            <CardTitle className={config.color}>{config.label}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <HealthScore status={status} divergence={divergence_percent} />
          <p className="text-sm text-slate-600 mt-4">
            Divergência: <strong>{divergence_percent.toFixed(1)}%</strong>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Diferença entre eventos Pixel e conversões reais
          </p>
        </CardContent>
      </Card>

      {/* Event Breakdown */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            Breakdown de Eventos
          </CardTitle>
          <p className="text-xs text-slate-600 mt-1">Total: {total_events} eventos rastreados</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* CAPI Events */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">CAPI (Server-side)</span>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Mais preciso</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{api_events}</p>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full bg-purple-500"
                  style={{ width: `${apiPercent}%` }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-1">{apiPercent}% do total</p>
            </div>

            {/* Browser Events */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">Browser</span>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">Client-side</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{browser_events}</p>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full bg-indigo-500"
                  style={{ width: `${browserPercent}%` }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-1">{browserPercent}% do total</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6 text-sm space-y-2">
          <p className="text-blue-900">
            💡 <strong>CAPI (Server)</strong> é mais confiável pois confirma conversões no backend.
            <strong> Browser</strong> rastreia visitantes no frontend.
          </p>
          <p className="text-blue-900">
            📊 Uma pequena divergência é normal. Acima de 15% indica possível problema no rastreamento.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
